import { useState } from "react";
import { X, Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMessage } from "../lib/api";

const MODELS = [
  { id: "claude-sonnet-4-5", label: "Claude Sonnet 4.5" },
  { id: "gpt-5.2", label: "GPT-5.2" },
];

export default function AIInsightsPanel({ market, properties, city, county, state, onClose }) {
  const [model, setModel] = useState("claude-sonnet-4-5");
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState("");

  const run = async () => {
    setLoading(true);
    setInsights("");
    try {
      const r = await api.post("/ai/insights", {
        city,
        county,
        state,
        model,
        market_summary: market,
        top_properties: properties.slice(0, 5),
      });
      setInsights(r.data.insights);
    } catch (e) {
      toast.error(errMessage(e, "AI insights failed"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="ai-insights-panel"
      className="fixed inset-0 z-50 bg-black/40 flex justify-end"
      onClick={onClose}
    >
      <aside
        className="w-full max-w-md h-full bg-white border-l border-[--ink] flex flex-col animate-fade-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-b border-[--line] p-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles size={16} strokeWidth={1.5} className="text-[--accent]" />
            <div className="font-display font-bold text-lg">AI market read</div>
          </div>
          <button
            data-testid="close-ai-panel"
            onClick={onClose}
            className="p-1 hover:bg-[--bg-soft]"
            aria-label="Close"
          >
            <X size={16} strokeWidth={1.5} />
          </button>
        </div>

        <div className="p-5 border-b border-[--line]">
          <div className="overline mb-2">Model</div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {MODELS.map((m) => (
              <button
                key={m.id}
                data-testid={`model-${m.id}`}
                onClick={() => setModel(m.id)}
                className={`text-xs py-2 px-3 border transition-colors ${
                  model === m.id
                    ? "border-[--ink] bg-[--ink] text-white"
                    : "border-[--line] hover:border-[--ink]"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
          <button
            data-testid="run-insights-button"
            onClick={run}
            disabled={loading}
            className="btn-primary w-full flex items-center justify-center gap-2"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} strokeWidth={1.5} />}
            {loading ? "Analyzing…" : "Run analysis"}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 text-sm leading-relaxed text-[--ink]">
          {!insights && !loading && (
            <div className="text-[--ink-muted] text-sm">
              Pick a model and run analysis. The AI will read this market's metrics and
              the top 5 ranked properties, then return actionable bullets.
            </div>
          )}
          {loading && (
            <div className="flex items-center gap-2 text-[--ink-muted]">
              <div className="w-1.5 h-1.5 bg-[--accent] animate-pulse" />
              Reading market signals…
            </div>
          )}
          {insights && (
            <pre
              data-testid="ai-insights-output"
              className="whitespace-pre-wrap font-sans text-sm leading-relaxed"
            >
              {insights}
            </pre>
          )}
        </div>
      </aside>
    </div>
  );
}
